/**
 * QuizNotificationModal Component
 * Allows admin to customize the notification sent when starting a quiz
 * Matches the pattern used in notification settings (title + lottie)
 */

import React, { useState, useCallback, useMemo, useEffect, useRef, memo } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Switch,
  Dimensions,
  Image,
  FlatList,
  ViewToken,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import LottieView from "lottie-react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { 
  getAllLotties, 
  getLottieSource, 
  LottieItem, 
  LottieCategory,
  LOTTIE_CATEGORY_LABELS 
} from "@/lib/lottie-registry";
import { QuizNotificationConfig, QuizMaster } from "../types";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

// Calculate item size for 5 columns with padding
const ITEMS_PER_ROW = 5;
const ITEM_SIZE = (SCREEN_WIDTH - 48) / ITEMS_PER_ROW;

// Categories relevant for notifications (ordered for display) - defined outside component for stability
const NOTIFICATION_CATEGORIES: LottieCategory[] = [
  "celebration",
  "emoji", 
  "animal",
  "monster",
  "fruit",
  "action",
  "object",
  "people",
];

/**
 * Row item for FlatList - contains category header or lottie row
 */
type PickerRowItem = 
  | { type: "header"; category: LottieCategory }
  | { type: "row"; items: LottieItem[] };

/**
 * Memoized Lottie item component with lazy animation
 * Only plays animation when visible in viewport
 */
const LottiePickerItem = memo(({ 
  lottie, 
  isSelected, 
  isVisible,
  onSelect, 
  colors 
}: { 
  lottie: LottieItem; 
  isSelected: boolean; 
  isVisible: boolean;
  onSelect: (key: string) => void;
  colors: ReturnType<typeof useThemeColors>;
}) => {
  return (
    <TouchableOpacity
      onPress={() => onSelect(lottie.key)}
      className="items-center mb-3"
      style={{ width: ITEM_SIZE }}
      accessibilityLabel={`Selecteer ${lottie.name}`}
      accessibilityRole="button"
    >
      <View
        className="rounded-xl overflow-hidden items-center justify-center"
        style={{
          width: ITEM_SIZE - 8,
          height: ITEM_SIZE - 8,
          backgroundColor: isSelected ? colors.primary + "30" : colors.surface,
          borderWidth: isSelected ? 2 : 1,
          borderColor: isSelected ? colors.primary : colors.border,
        }}
      >
        {isVisible ? (
          <LottieView
            source={lottie.source}
            autoPlay
            loop
            style={{ width: ITEM_SIZE - 16, height: ITEM_SIZE - 16 }}
          />
        ) : (
          <View 
            style={{ 
              width: ITEM_SIZE - 16, 
              height: ITEM_SIZE - 16,
              backgroundColor: colors.surfaceSecondary,
              borderRadius: 8,
            }} 
          />
        )}
        {isSelected && (
          <View 
            className="absolute top-1 right-1 w-5 h-5 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primary }}
          >
            <Ionicons name="checkmark" size={14} color="#FFFFFF" />
          </View>
        )}
      </View>
      <Text 
        className="text-xs text-center mt-1"
        style={{ color: colors.textSecondary }}
        numberOfLines={1}
      >
        {lottie.name}
      </Text>
    </TouchableOpacity>
  );
});

/**
 * Optimized Lottie Picker Modal with FlatList virtualization
 * Only animates visible items for better performance
 */
const LottiePickerModal = memo(({
  visible,
  selectedLottie,
  lottiesByCategory,
  onSelect,
  onClose,
  colors,
}: {
  visible: boolean;
  selectedLottie: string;
  lottiesByCategory: Partial<Record<LottieCategory, LottieItem[]>>;
  onSelect: (key: string) => void;
  onClose: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) => {
  // Track which lottie keys are currently visible in viewport
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  
  // Build flat list data with headers and rows
  const flatListData = useMemo((): PickerRowItem[] => {
    const data: PickerRowItem[] = [];
    
    for (const category of NOTIFICATION_CATEGORIES) {
      const items = lottiesByCategory[category];
      if (!items || items.length === 0) continue;
      
      // Add category header
      data.push({ type: "header", category });
      
      // Group items into rows of ITEMS_PER_ROW
      const validItems = items.filter(l => l.source != null);
      for (let i = 0; i < validItems.length; i += ITEMS_PER_ROW) {
        data.push({ 
          type: "row", 
          items: validItems.slice(i, i + ITEMS_PER_ROW) 
        });
      }
    }
    
    return data;
  }, [lottiesByCategory]);
  
  // Handle viewable items change - update which lotties should animate
  const onViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    const newVisibleKeys = new Set<string>();
    
    for (const viewable of viewableItems) {
      const item = viewable.item as PickerRowItem;
      if (item.type === "row") {
        for (const lottie of item.items) {
          newVisibleKeys.add(lottie.key);
        }
      }
    }
    
    setVisibleKeys(newVisibleKeys);
  }).current;
  
  // Viewability config - items are considered visible when 10% is in view
  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 10,
    minimumViewTime: 100,
  }).current;
  
  // Get unique key for each row
  const getItemKey = useCallback((item: PickerRowItem, index: number): string => {
    if (item.type === "header") {
      return `header-${item.category}`;
    }
    return `row-${index}-${item.items.map(l => l.key).join("-")}`;
  }, []);
  
  // Render a single row (header or lottie row)
  const renderItem = useCallback(({ item }: { item: PickerRowItem }) => {
    if (item.type === "header") {
      return (
        <View className="mb-3 mt-2">
          <Text 
            className="text-lg font-semibold" 
            style={{ color: colors.text }}
          >
            {LOTTIE_CATEGORY_LABELS[item.category] || item.category}
          </Text>
        </View>
      );
    }
    
    return (
      <View className="flex-row">
        {item.items.map((lottie) => (
          <LottiePickerItem
            key={lottie.key}
            lottie={lottie}
            isSelected={selectedLottie === lottie.key}
            isVisible={visibleKeys.has(lottie.key)}
            onSelect={onSelect}
            colors={colors}
          />
        ))}
      </View>
    );
  }, [selectedLottie, visibleKeys, onSelect, colors]);
  
  // Reset visible keys when modal closes
  useEffect(() => {
    if (!visible) {
      setVisibleKeys(new Set());
    }
  }, [visible]);
  
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView 
        className="flex-1" 
        style={{ backgroundColor: colors.background }}
        edges={["top", "bottom"]}
      >
        {/* Picker Header */}
        <View 
          className="px-4 py-4 flex-row items-center justify-between border-b"
          style={{ borderBottomColor: colors.border }}
        >
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            Kies een Animatie
          </Text>
          <TouchableOpacity 
            onPress={onClose}
            accessibilityLabel="Sluiten"
            accessibilityRole="button"
          >
            <Ionicons name="close" size={28} color={colors.text} />
          </TouchableOpacity>
        </View>
        
        {/* Virtualized Lottie Grid */}
        <FlatList
          data={flatListData}
          renderItem={renderItem}
          keyExtractor={getItemKey}
          contentContainerStyle={{ padding: 16, paddingBottom: 32 }}
          showsVerticalScrollIndicator={false}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
          removeClippedSubviews={true}
          maxToRenderPerBatch={5}
          windowSize={5}
          initialNumToRender={8}
          getItemLayout={(data, index) => ({
            length: data?.[index]?.type === "header" ? 40 : ITEM_SIZE + 12,
            offset: index * (ITEM_SIZE + 12),
            index,
          })}
        />
      </SafeAreaView>
    </Modal>
  );
});

interface QuizNotificationModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Quiz name for default title */
  quizName: string;
  /** Called when admin confirms */
  onConfirm: (config: QuizNotificationConfig) => void;
  /** Called when modal is closed */
  onClose: () => void;
}

/**
 * Default title for quiz start notification
 */
const getDefaultTitle = (quizName: string): string => {
  return `🎯 ${quizName} begint nu!`;
};

/**
 * QuizNotificationModal - Customize quiz start notification
 */
export const QuizNotificationModal: React.FC<QuizNotificationModalProps> = ({
  visible,
  quizName,
  onConfirm,
  onClose,
}) => {
  const colors = useThemeColors();
  const { activeProfile } = useAuth();
  
  // State
  const [title, setTitle] = useState(getDefaultTitle(quizName));
  const [selectedLottie, setSelectedLottie] = useState("party");
  const [sendPush, setSendPush] = useState(true);
  const [showLottiePicker, setShowLottiePicker] = useState(false);
  const [showQuizMasterPicker, setShowQuizMasterPicker] = useState(false);
  
  // Quiz Master state
  const [availableAdmins, setAvailableAdmins] = useState<QuizMaster[]>([]);
  const [selectedQuizMaster, setSelectedQuizMaster] = useState<QuizMaster | null>(null);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  
  // Fetch available admins (familie_admin and super_admin)
  useEffect(() => {
    const fetchAdmins = async () => {
      if (!visible) return;
      
      setLoadingAdmins(true);
      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select(`
            user_id,
            role,
            profile:profiles!user_id (
              id,
              full_name,
              avatar_url
            )
          `)
          .in("role", ["super_admin", "familie_admin"])
          .is("revoked_at", null);
        
        if (error) throw error;
        
        const admins: QuizMaster[] = (data || [])
          .filter((r: any) => r.profile)
          .map((r: any) => ({
            user_id: r.user_id,
            full_name: r.profile.full_name || "Admin",
            avatar_url: r.profile.avatar_url,
          }));
        
        // Remove duplicates (user might have multiple admin roles)
        const uniqueAdmins = admins.filter((admin, index, self) =>
          index === self.findIndex(a => a.user_id === admin.user_id)
        );
        
        setAvailableAdmins(uniqueAdmins);
        
        // Default to current user as quiz master
        if (activeProfile?.id) {
          const currentAdmin = uniqueAdmins.find(a => a.user_id === activeProfile.id);
          if (currentAdmin) {
            setSelectedQuizMaster(currentAdmin);
          } else if (uniqueAdmins.length > 0) {
            setSelectedQuizMaster(uniqueAdmins[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching admins:", err);
      } finally {
        setLoadingAdmins(false);
      }
    };
    
    fetchAdmins();
  }, [visible, activeProfile?.id]);
  
  // Group lotties by category - only include items with valid sources
  const lottiesByCategory = useMemo(() => {
    const allLotties = getAllLotties();
    const grouped: Partial<Record<LottieCategory, LottieItem[]>> = {};
    
    for (const category of NOTIFICATION_CATEGORIES) {
      // Filter to only include items with valid sources
      const items = allLotties.filter(l => l.category === category && l.source != null);
      if (items.length > 0) {
        grouped[category] = items;
      }
    }
    
    return grouped;
  }, []);
  
  /**
   * Handle confirm
   */
  const handleConfirm = useCallback(() => {
    onConfirm({
      title,
      lottie_animation: selectedLottie,
      send_push: sendPush,
      quiz_master: selectedQuizMaster || undefined,
    });
  }, [title, selectedLottie, sendPush, selectedQuizMaster, onConfirm]);
  
  /**
   * Get selected lottie item
   */
  const getSelectedLottieItem = useCallback((): LottieItem | undefined => {
    return getAllLotties().find(l => l.key === selectedLottie);
  }, [selectedLottie]);
  
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View className="flex-1 justify-end bg-black/60">
        <SafeAreaView 
          className="rounded-t-3xl max-h-[90%]"
          style={{ backgroundColor: colors.surface }}
          edges={["bottom"]}
        >
          {/* Header */}
          <View className="flex-row items-center justify-between px-6 pt-6 pb-4">
            <Text 
              className="text-xl font-bold"
              style={{ color: colors.text }}
            >
              Quiz Notificatie
            </Text>
            <TouchableOpacity
              onPress={onClose}
              className="p-2"
              accessibilityLabel="Sluiten"
            >
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>
          
          <ScrollView 
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingHorizontal: 24 }}
          >
            {/* Title Input */}
            <View className="mb-6">
              <Text 
                className="text-sm font-medium mb-2"
                style={{ color: colors.textSecondary }}
              >
                Notificatie Titel
              </Text>
              <TextInput
                value={title}
                onChangeText={setTitle}
                placeholder="Bijv. 🎯 Quiz begint nu!"
                placeholderTextColor={colors.textTertiary}
                className="p-4 rounded-xl text-base"
                style={{ 
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                }}
                accessibilityLabel="Notificatie titel"
              />
            </View>
            
            {/* Lottie Selection */}
            <View className="mb-6">
              <Text 
                className="text-sm font-medium mb-2"
                style={{ color: colors.textSecondary }}
              >
                Animatie
              </Text>
              <TouchableOpacity
                onPress={() => setShowLottiePicker(!showLottiePicker)}
                className="flex-row items-center p-4 rounded-xl"
                style={{ backgroundColor: colors.surfaceSecondary }}
                accessibilityLabel="Selecteer animatie"
              >
                <View 
                  className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                  style={{ backgroundColor: colors.primary + "20" }}
                >
                  {getLottieSource(selectedLottie) ? (
                    <LottieView
                      source={getLottieSource(selectedLottie)}
                      autoPlay
                      loop
                      style={{ width: 40, height: 40 }}
                    />
                  ) : (
                    <Ionicons name="sparkles" size={24} color={colors.primary} />
                  )}
                </View>
                <View className="flex-1">
                  <Text style={{ color: colors.text }}>
                    {getSelectedLottieItem()?.name || selectedLottie}
                  </Text>
                </View>
                <Ionicons 
                  name={showLottiePicker ? "chevron-up" : "chevron-down"} 
                  size={20} 
                  color={colors.textTertiary} 
                />
              </TouchableOpacity>
              
              {/* Lottie Picker - Optimized Full Modal */}
              <LottiePickerModal
                visible={showLottiePicker}
                selectedLottie={selectedLottie}
                lottiesByCategory={lottiesByCategory}
                onSelect={(key) => {
                  setSelectedLottie(key);
                  setShowLottiePicker(false);
                }}
                onClose={() => setShowLottiePicker(false)}
                colors={colors}
              />
            </View>
            
            {/* Quiz Master Selection */}
            <View className="mb-6">
              <Text 
                className="text-sm font-medium mb-2"
                style={{ color: colors.textSecondary }}
              >
                Quiz Master
              </Text>
              <Text 
                className="text-xs mb-3"
                style={{ color: colors.textTertiary }}
              >
                De quiz master beheert de quiz en bedient het grote scherm
              </Text>
              
              {loadingAdmins ? (
                <View 
                  className="p-4 rounded-xl items-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Text style={{ color: colors.textSecondary }}>Laden...</Text>
                </View>
              ) : availableAdmins.length === 0 ? (
                <View 
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Text style={{ color: colors.textSecondary }}>
                    Geen familie admins gevonden
                  </Text>
                </View>
              ) : (
                <View className="gap-2">
                  {availableAdmins.map((admin) => {
                    const isSelected = selectedQuizMaster?.user_id === admin.user_id;
                    const isMe = admin.user_id === activeProfile?.id;
                    
                    return (
                      <TouchableOpacity
                        key={admin.user_id}
                        onPress={() => setSelectedQuizMaster(admin)}
                        className="flex-row items-center p-3 rounded-xl"
                        style={{
                          backgroundColor: isSelected ? colors.primary + "20" : colors.surfaceSecondary,
                          borderWidth: isSelected ? 2 : 1,
                          borderColor: isSelected ? colors.primary : colors.border,
                        }}
                        accessibilityLabel={`Selecteer ${admin.full_name} als quiz master`}
                      >
                        {admin.avatar_url ? (
                          <Image
                            source={{ uri: admin.avatar_url }}
                            className="w-10 h-10 rounded-full mr-3"
                          />
                        ) : (
                          <View 
                            className="w-10 h-10 rounded-full mr-3 items-center justify-center"
                            style={{ backgroundColor: colors.primary + "30" }}
                          >
                            <Ionicons name="person" size={20} color={colors.primary} />
                          </View>
                        )}
                        <View className="flex-1">
                          <View className="flex-row items-center">
                            <Text 
                              className="font-medium"
                              style={{ color: colors.text }}
                            >
                              {admin.full_name}
                            </Text>
                            {isMe && (
                              <View 
                                className="ml-2 px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: colors.primary + "20" }}
                              >
                                <Text 
                                  className="text-xs"
                                  style={{ color: colors.primary }}
                                >
                                  Jij
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                        {isSelected && (
                          <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
            
            {/* Push Notification Toggle */}
            <View className="mb-6">
              <View className="flex-row items-center justify-between">
                <View className="flex-1 mr-4">
                  <Text 
                    className="font-medium"
                    style={{ color: colors.text }}
                  >
                    Push Notificatie
                  </Text>
                  <Text 
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Stuur ook als app niet open is
                  </Text>
                </View>
                <Switch
                  value={sendPush}
                  onValueChange={setSendPush}
                  trackColor={{ false: colors.border, true: colors.primary + "60" }}
                  thumbColor={sendPush ? colors.primary : colors.textTertiary}
                />
              </View>
            </View>
            
            {/* Preview */}
            <View className="mb-6">
              <Text 
                className="text-sm font-medium mb-2"
                style={{ color: colors.textSecondary }}
              >
                Voorbeeld
              </Text>
              <View 
                className="p-4 rounded-xl flex-row items-center"
                style={{ backgroundColor: colors.primary + "10" }}
              >
                <View 
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: colors.primary + "20" }}
                >
                  {getLottieSource(selectedLottie) ? (
                    <LottieView
                      source={getLottieSource(selectedLottie)}
                      autoPlay
                      loop
                      style={{ width: 32, height: 32 }}
                    />
                  ) : (
                    <Ionicons name="sparkles" size={20} color={colors.primary} />
                  )}
                </View>
                <View className="flex-1">
                  <Text 
                    className="font-semibold"
                    style={{ color: colors.text }}
                  >
                    {title}
                  </Text>
                  <Text 
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    De {quizName} begint nu! Doe snel mee.
                  </Text>
                </View>
              </View>
            </View>
          </ScrollView>
          
          {/* Actions */}
          <View className="flex-row space-x-3 px-6 pb-6 pt-4">
            <TouchableOpacity
              onPress={onClose}
              className="flex-1 py-4 rounded-xl items-center"
              style={{ backgroundColor: colors.surfaceSecondary }}
              accessibilityLabel="Annuleren"
            >
              <Text style={{ color: colors.text, fontWeight: "600" }}>
                Annuleren
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleConfirm}
              className="flex-1 py-4 rounded-xl items-center ml-3"
              style={{ backgroundColor: colors.primary }}
              accessibilityLabel="Sessie starten"
            >
              <Text className="font-semibold text-white">
                Sessie Starten
              </Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
};

export default QuizNotificationModal;

